import { Category } from '../types';

/**
 * Groups a flat list of categories into a tree structure.
 * Main categories (parent_id is null) become root nodes,
 * sub-categories are nested under their parent.
 */
export function buildCategoryTree(categories: Category[]): Category[] {
  const mainCategories = categories.filter(c => !c.parent_id);
  return mainCategories.map(main => ({
    ...main,
    subcategories: categories.filter(c => c.parent_id === main.id),
  }));
}

/**
 * Returns the category id plus all its sub-category ids.
 * Used for filtering products by a main category (includes all subs).
 */
export function getCategoryFilterIds(
  categoryId: number,
  allCategories: Category[]
): number[] {
  const subs = allCategories
    .filter(c => c.parent_id === categoryId)
    .map(c => c.id);
  return [categoryId, ...subs];
}

/**
 * Resolves which category_id to store on a product.
 * If a sub-category is selected, use it; otherwise use the main category.
 */
export function resolveCategoryId(
  mainCategoryId: number | null,
  subCategoryId: number | null
): number | null {
  return subCategoryId ?? mainCategoryId;
}

/**
 * Given a category id, finds its parent_id from the flat list.
 * Returns null if the category is a main category or not found.
 */
export function findParentCategoryId(
  categoryId: number,
  allCategories: Category[]
): number | null {
  const category = allCategories.find(c => c.id === categoryId);
  return category?.parent_id ?? null;
}
